import axios from 'axios';
import { env } from '../config/env';

export function getStoredToken() {
  return localStorage.getItem('token');
}

export function storeAuthSession(payload) {
  if (payload?.access_token) {
    localStorage.setItem('token', payload.access_token);
  }

  if (payload?.refresh_token) {
    localStorage.setItem('refresh_token', payload.refresh_token);
  }

  if (payload?.user) {
    localStorage.setItem('current_user', JSON.stringify(payload.user));
  }
}

export function clearStoredSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('current_user');
}

const api = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    const isAuthEndpoint =
      requestUrl.includes('/login') ||
      requestUrl.includes('/register') ||
      requestUrl.includes('/token/refresh');

    if (status === 401 && !isAuthEndpoint && getStoredToken()) {
      clearStoredSession();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper functions
export const getStatus = async () => {
  try {
    const response = await api.get('/status');
    return response.data;
  } catch (error) {
    console.error('Error fetching status:', error);
    throw error;
  }
};

export const getTradingData = async (
  symbol,
  capital = 10000,
  initialPurchasePercentage = 25,
  supportPurchasePercentage = 15
) => {
  try {
    const response = await api.get(
      `/trading/trading_data?symbol=${symbol}&capital=${capital}&initial_purchase_percentage=${initialPurchasePercentage}&support_purchase_percentage=${supportPurchasePercentage}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching trading data:', error);
    throw error;
  }
};

export const getPrediction = async (symbol) => {
  try {
    const response = await api.post('/trading/predict', { symbol });
    return response.data;
  } catch (error) {
    console.error('Error fetching prediction:', error);
    throw error;
  }
};


export const getUserFeatures = async () => {
  try {
    const response = await api.get('/user/features');
    return response.data;
  } catch (error) {
    console.error('Error fetching user features:', error);
    throw error;
  }
};

export const getUserPreferences = async () => {
  try {
    const response = await api.get('/user/preferences');
    return response.data;
  } catch (error) {
    console.error('Error fetching user preferences:', error);
    throw error;
  }
};

export const saveUserPreferences = async (preferences) => {
  try {
    const response = await api.post('/user/preferences', preferences);
    return response.data;
  } catch (error) {
    console.error('Error saving user preferences:', error);
    throw error;
  }
};

export const savePreferences = async (preferences) => {
  try {
    const response = await api.post('/user/preferences', preferences);
    return response.data;
  } catch (error) {
    console.error('Error saving user preferences:', error);
    throw error;
  }
};

// Attach helper functions to the default export if you want them available as methods
api.getStatus = getStatus;
api.getTradingData = getTradingData;
api.getPrediction = getPrediction;
api.getUserFeatures = getUserFeatures;
api.getUserPreferences = getUserPreferences;
api.saveUserPreferences = saveUserPreferences;
api.savePreferences = savePreferences;

export default api;
